<template>
    <app-layout title="Pengadaan Diajukan Menang">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Pengadaan Diajukan Menang
            </h2>
        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <DataTable 
                        ref="dt" 
                        :value="items.data" 
                        :lazy="true" 
                        data-key="id" 
                        :paginator="true" 
                        :rows="10"
                        :loading="loading" 
                        :total-records="items.total" 
                        filter-display="menu"
                        :global-filter-fields="['paket_pengadaan.nama_paket_pengadaan']"
                        v-model:filters="filters"
                        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        :rows-per-page-options="[5, 10, 25]"
                        current-page-report-template="Menampilkan data ke {first} sampai {last} dari total {totalRecords} data"
                        @page="onPage($event)"
                        class="p-datatable-sm">
                        
                        <template #header>
                            <div class="flex justify-content-between align-items-center">
                                <h5 class="m-0">Daftar Pengadaan Diajukan Menang</h5>
                                <span class="p-input-icon-left">
                                    <i class="pi pi-search" />
                                    <InputText 
                                        v-model="filters['global'].value" 
                                        placeholder="Cari nama paket..." 
                                        @input="onGlobalFilter" />
                                </span>
                            </div>
                        </template>

                        <Column field="paket_pengadaan.nama_paket_pengadaan" header="Nama Paket" sortable style="min-width: 200px">
                            <template #body="slotProps">
                                {{ slotProps.data.paket_pengadaan?.nama_paket_pengadaan || '-' }}
                            </template>
                        </Column>

                        <Column field="tahap.nama_tahap" header="Tahap" sortable style="min-width: 150px">
                            <template #body="slotProps">
                                {{ slotProps.data.tahap?.nama_tahap || '-' }}
                            </template>
                        </Column>

                        <Column field="status" header="Status" sortable style="min-width: 100px">
                            <template #body="slotProps">
                                <span 
                                    :class="getStatusClass(slotProps.data.status)"
                                    class="px-2 py-1 text-xs font-medium rounded-full">
                                    {{ slotProps.data.status }}
                                </span>
                            </template>
                        </Column>

                        <Column field="total_hps" header="Total HPS" sortable style="min-width: 150px">
                            <template #body="slotProps">
                                {{ formatCurrency(slotProps.data.total_hps) }}
                            </template>
                        </Column>

                        <Column field="pemenang_info" header="Pemenang" style="min-width: 200px">
                            <template #body="slotProps">
                                <div v-if="getPemenang(slotProps.data)">
                                    <div class="text-sm font-medium text-green-600">
                                        {{ getPemenang(slotProps.data).penyedia?.nama_perusahaan }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ formatCurrency(getTotalPenawaran(getPemenang(slotProps.data))) }}
                                    </div>
                                </div>
                                <span v-else class="text-gray-400">Belum ada pemenang</span>
                            </template>
                        </Column>

                        <Column header="Aksi" style="min-width: 200px">
                            <template #body="slotProps">
                                <div class="flex gap-2">
                                    <Button
                                        icon="pi pi-eye"
                                        label="Detail"
                                        severity="info"
                                        size="small"
                                        @click="viewDetail(slotProps.data)"
                                        text />
                                </div>
                            </template>
                        </Column>

                        <template #empty>
                            <div class="text-center py-4 text-gray-500">
                                Tidak ada data pengadaan diajukan menang
                            </div>
                        </template>
                    </DataTable>
                </div>
            </div>
        </div>

        <Toast position="top-right" />
    </app-layout>
</template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import { FilterMatchMode } from 'primevue/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

export default defineComponent({
    components: {
        AppLayout,
        DataTable,
        Column,
        InputText,
        Button,
        Toast,
    },

    setup() {
        const toast = useToast()
        return { toast }
    },

    data() {
        return {
            items: {
                data: [],
                total: 0
            },
            loading: false,
            filters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            },
            searchTimeout: null,
        }
    },

    methods: {
        async loadData(page = 1, rows = 10, globalFilter = '') {
            try {
                this.loading = true
                const params = {
                    page,
                    per_page: rows,
                    search: globalFilter
                }

                const response = await axios.get(route('dokumen_pengadaan.diajukan_menang.data'), { params })
                
                // The API returns { success: true, payload: { data: [...], total: ... } }
                if (response.data.success && response.data.payload) {
                    this.items = response.data.payload
                } else {
                    this.items = { data: [], total: 0 }
                }
            } catch (error) {
                console.error('Error loading data:', error)
                this.toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat data pengadaan',
                    life: 3000
                })
                this.items = { data: [], total: 0 }
            } finally {
                this.loading = false
            }
        },

        onPage(event) {
            const page = event.page + 1
            const rows = event.rows
            const globalFilter = this.filters.global.value || ''
            this.loadData(page, rows, globalFilter)
        },

        onGlobalFilter() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                const globalFilter = this.filters.global.value || ''
                this.loadData(1, 10, globalFilter)
            }, 300)
        },

        viewDetail(dokumen) {
            // Navigate to detail page
            this.$inertia.visit(route('dokumen_pengadaan.diajukan_menang.detail', dokumen.id))
        },

        formatCurrency(value) {
            if (value === null || value === undefined) return 'Rp 0'
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(value)
        },

        getStatusClass(status) {
            const statusClasses = {
                'draft': 'bg-gray-100 text-gray-800',
                'berlangsung': 'bg-blue-100 text-blue-800',
                'selesai': 'bg-green-100 text-green-800',
                'batal': 'bg-red-100 text-red-800',
                'revisi': 'bg-yellow-100 text-yellow-800',
                'direvisi': 'bg-orange-100 text-orange-800',
            }
            return statusClasses[status] || 'bg-gray-100 text-gray-800'
        },

        getPemenang(dokumen) {
            if (!dokumen.kepersertaan_pengadaan) return null
            return dokumen.kepersertaan_pengadaan.find(k => k.is_pemenang) || null
        },

        getTotalPenawaran(kepersertaan) {
            if (!kepersertaan?.detail_penawaran_kepersertaan_pengadaan) return 0
            return kepersertaan.detail_penawaran_kepersertaan_pengadaan.reduce((sum, item) => 
                sum + (item.volume * item.harga_satuan), 0)
        },
    },

    mounted() {
        this.loadData()
    }
})
</script>

<style scoped>
.p-datatable-sm .p-datatable-thead > tr > th {
    padding: 0.5rem;
}

.p-datatable-sm .p-datatable-tbody > tr > td {
    padding: 0.5rem;
}
</style> 